import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getIncomingNumbers,
  searchAvailableNumbers,
  provisionNumber,
  getPhoneNumbers,
} from '@/services/phone-numbers-service/phone-numbers-service';
import {
  SearchAvailableParams,
  ProvisionNumberPayload,
} from '@/services/phone-numbers-service/phone-numbers-service.types';

export const useIncomingNumbers = (companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.INCOMING_NUMBERS(companyId),
    queryFn: () => getIncomingNumbers({ company_id: companyId! }),
    enabled: !!companyId,
  });

  return { incomingNumbers: data?.incoming_numbers, isPending };
};

export const useSearchAvailableNumbers = (
  params: SearchAvailableParams,
  enabled: boolean = false
) => {
  const { data, isPending, isFetching } = useQuery({
    queryKey: ['available-numbers', params],
    queryFn: () => searchAvailableNumbers(params),
    enabled,
  });

  return { availableNumbers: data?.available_numbers, isPending, isFetching };
};

export const useProvisionNumber = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const {
    mutate: provision,
    isPending,
    isSuccess,
    error,
  } = useMutation({
    mutationFn: (data: ProvisionNumberPayload) => provisionNumber(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.INCOMING_NUMBERS(companyId),
      });
    },
  });

  return { provision, isPending, isSuccess, error };
};

export const useCompanyPhoneNumbers = (companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ['company-phone-numbers', companyId],
    queryFn: () => getPhoneNumbers({ company_id: companyId! }),
    enabled: !!companyId,
  });

  return { phoneNumbers: data?.twilio_phone_numbers, isPending };
};